import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
} from "@angular/core";
import {
  AccountSelectionService,
  AdStatsType,
  AuthService,
  isReadOnlyAccess,
  MetricsSelectorKey,
  UserSelectionService,
} from "@front/m19-services";
import { map } from "rxjs";

import { CurrencyStat } from "@front/m19-models";
import { AccountMarketplace } from "@front/m19-api-client";
import { Metric } from "@front/m19-metrics";
import { TranslocoModule } from "@jsverse/transloco";
import { IDropdownMenuTriggerDirective, IIconComponent } from "@front/m19-ui";
import { toSignal } from "@angular/core/rxjs-interop";
import { MetricCardComponent } from "@m19-board/insights/metric-selector/metric-card/metric-card.component";
import { MetricsMenuComponent } from "@m19-board/insights/metric-selector/metrics-menu/metrics-menu.component";

export const MAX_METRICS = 10;

@Component({
  selector: "app-metric-selector",
  imports: [TranslocoModule, MetricCardComponent, MetricsMenuComponent, IIconComponent, IDropdownMenuTriggerDirective],
  templateUrl: "./metric-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricSelectorComponent<T extends CurrencyStat> {
  private userSelectionService = inject(UserSelectionService);
  private authService = inject(AuthService);
  private accountSelectionService = inject(AccountSelectionService);
  readonly MAX_METRICS = MAX_METRICS;

  data = input.required<T>();
  previousPeriodData = input<T | undefined>(undefined);
  availableMetrics = input.required<Metric<T>[]>();
  singleMetricMode = input(false);
  localStorageKey = input.required<MetricsSelectorKey>();
  showCompare = input(true);
  splitData = input<{ splitData1: T; splitData2: T; type: AdStatsType } | undefined>(undefined);
  previousSplitData = input<{ splitData1: T; splitData2: T; type: AdStatsType } | undefined>(undefined);
  dataLoading = input<boolean>(false);

  selectedMetrics = input<Metric<T>[]>([]);
  selectedMetricsChange = output<Metric<T>[]>();

  shouldRequestSellingPartnerGrant = toSignal(
    this.accountSelectionService.accountMarketplacesSelection$.pipe(
      map((accountMarketplaces: AccountMarketplace[]) =>
        accountMarketplaces.some((am) => !am.isValidToken && !isReadOnlyAccess(am.accessLevel)),
      ),
    ),
    { requireSync: true },
  );

  metricsInSelector = linkedSignal<Metric<T>[], Metric<T>[]>({
    source: this.availableMetrics,
    computation: (availableMetrics) => {
      let metricsInSelector: Metric<T>[] = this.userSelectionService
        .getMetricsInSelectorFromLocalStorage(this.localStorageKey())
        .filter((m) => availableMetrics.includes(m));

      if (!metricsInSelector || !metricsInSelector.length) metricsInSelector = availableMetrics.slice(0, 6);
      return metricsInSelector;
    },
  });

  compatibleSelectedMetrics = linkedSignal<Metric<T>[], Metric<T>[]>({
    source: this.selectedMetrics,
    computation: (selectedMetrics) => {
      selectedMetrics = selectedMetrics.filter((m) => this.isMetricInSelector(m));

      if (this.shouldRequestSellingPartnerGrant()) {
        selectedMetrics = selectedMetrics.filter((m) => !m.requireSellingPartnerAccess);
      }

      if (selectedMetrics.length === 0) {
        return this.getDefaultMetricsSelection(this.shouldRequestSellingPartnerGrant());
      }
      return selectedMetrics;
    },
  });

  locale = toSignal(this.authService.userLocale$, { requireSync: true });
  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  periodComparison = toSignal(this.userSelectionService.periodComparison$.pipe(map((pc) => pc?.period)));
  loadingPreviousPeriodMetrics = computed(() => {
    return (
      this.periodComparison() &&
      (this.previousPeriodData() == undefined || !Object.keys(this.previousPeriodData()!).length)
    );
  });

  constructor() {
    effect(() => {
      this.selectedMetricsChange.emit(this.compatibleSelectedMetrics());
    });

    effect(() => {
      this.saveMetricsInSelectorToLocalStorage(this.metricsInSelector());
    });

    effect(() => {
      this.saveSelectedMetricsToLocalStorage(this.compatibleSelectedMetrics());
    });
  }

  toggleMetric(metric: Metric<T>): void {
    if (this.shouldRequestSellingPartnerGrant() && metric.requireSellingPartnerAccess) return;

    let selected = [...this.compatibleSelectedMetrics()];

    if (this.singleMetricMode()) {
      selected = [metric];
    } else {
      const idx = selected.indexOf(metric);
      const isMetricAlreadySelected = idx > -1;
      if (isMetricAlreadySelected) {
        if (selected.length === 1) return; // At least one metric must be selected
        selected.splice(idx, 1);
      } else {
        if (selected.length > 1) selected.shift();
        selected.push(metric);
      }
    }

    this.compatibleSelectedMetrics.set(selected);
  }

  toggleMetricFromDropdown(i: number, m: Metric<T>): void {
    if (this.metricsInSelector().length > this.MAX_METRICS) {
      return;
    }
    if (this.isMetricInSelector(m)) {
      this.removeMetricFromDropdown(m);
    } else {
      this.selectedMetricFromDropdown(i, m);
    }
  }

  private selectedMetricFromDropdown(index: number, metric: Metric<T>): void {
    const metricsInSelector = this.metricsInSelector();
    // Cannot add a new existing metric
    if (index === metricsInSelector.length && this.isMetricInSelector(metric)) return;

    // Handle switch of two metrics
    const inSelector = metricsInSelector.indexOf(metric);
    if (inSelector > -1) {
      metricsInSelector[inSelector] = metricsInSelector[index];
    }

    // Check if the metric we're about to replace is a selected chart metric
    // Keep same chart metrics if we did a metric switch
    const replacedMetric: Metric<T> = metricsInSelector[index];
    const isReplacedMetricChart = this.compatibleSelectedMetrics().indexOf(replacedMetric);

    if (isReplacedMetricChart > -1 && inSelector === -1) {
      // If so, select the new metric as chart metric at the same index
      this.compatibleSelectedMetrics.update((metrics) => {
        metrics[isReplacedMetricChart] = metric;
        return [...metrics];
      });
    }

    metricsInSelector[index] = metric;
    this.metricsInSelector.set([...metricsInSelector]);
  }

  private isMetricInSelector(m: Metric<T>): boolean {
    return this.metricsInSelector().includes(m);
  }

  removeMetricFromDropdown(m: Metric<T>): void {
    const index = this.metricsInSelector().indexOf(m);

    const metricsInSelector = this.metricsInSelector();
    const removedMetric: Metric<T> = metricsInSelector[index];
    const isRemovedMetricChart = this.compatibleSelectedMetrics().indexOf(removedMetric);
    if (isRemovedMetricChart > -1) {
      const selectedMetrics = this.compatibleSelectedMetrics();
      selectedMetrics.splice(isRemovedMetricChart, 1);
      this.compatibleSelectedMetrics.set([...selectedMetrics]);
    }
    this.metricsInSelector.set([...metricsInSelector.filter((m, i) => i != index)]);
  }

  private saveMetricsInSelectorToLocalStorage(metrics: Metric<T>[]): void {
    this.userSelectionService.setMetricsInSelectorInLocalStorage(metrics, this.localStorageKey());
  }

  private saveSelectedMetricsToLocalStorage(metrics: Metric<T>[]): void {
    this.userSelectionService.setSelectedMetricsInLocalStorage(metrics, this.localStorageKey());
  }

  private getDefaultMetricsSelection(filterSellingPartner: boolean): Metric<T>[] {
    let selectedMetrics: Metric<T>[] = this.userSelectionService
      .getSelectedMetricsFromLocalStorage(this.localStorageKey())
      .filter((m) => this.isMetricInSelector(m))
      .filter((m) => !filterSellingPartner || !m.requireSellingPartnerAccess)
      .slice(0, this.singleMetricMode() ? 1 : 2);

    if (!selectedMetrics || !selectedMetrics.length) {
      selectedMetrics = this.metricsInSelector()
        .filter((m) => !filterSellingPartner || !m.requireSellingPartnerAccess)
        .slice(0, this.singleMetricMode() ? 1 : 2);
    }
    return selectedMetrics;
  }
}
